'use client';

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  Screen,
  Language,
  MapScope,
  LearningMode,
  SubjectMap,
  Trail,
  BoardCell,
  Message,
  StudentProfile,
  SavedSession,
} from '../types';

// Default student profile
const createDefaultProfile = (): StudentProfile => ({
  id: crypto.randomUUID(),
  language: 'en',
  topicProgress: {},
  xp: 0,
  level: 1,
  streak: 0,
  lastActiveDate: new Date().toISOString().split('T')[0],
  achievements: [],
});

// Store interface
interface AppStore {
  // State
  screen: Screen;
  language: Language;
  topicInput: string;
  selectedScope: MapScope;
  subjectMap: SubjectMap | null;
  trail: Trail | null;
  selectedNodeId: string | null;
  mode: LearningMode;
  boardCells: BoardCell[];
  messages: Message[];
  isMapPanelOpen: boolean;
  profile: StudentProfile;
  isLoading: boolean;
  error: string | null;
  savedSessions: SavedSession[];
  currentSessionId: string | null;
  autoPlayTTS: boolean;
  hasPlayedFirstTTS: boolean;

  // Navigation
  setScreen: (screen: Screen) => void;
  setLanguage: (language: Language) => void;

  // Subject Selection
  setTopicInput: (input: string) => void;
  setSelectedScope: (scope: MapScope) => void;
  setSubjectMap: (map: SubjectMap | null) => void;
  setTrail: (trail: Trail | null) => void;
  setSelectedNodeId: (nodeId: string | null) => void;

  // Learning
  setMode: (mode: LearningMode) => void;
  addBoardCell: (cell: BoardCell) => void;
  updateBoardCell: (id: string, updates: Partial<BoardCell>) => void;
  clearBoard: () => void;
  addMessage: (message: Message) => void;
  clearMessages: () => void;
  toggleMapPanel: () => void;
  setMapPanelOpen: (open: boolean) => void;

  // Student Profile
  updateProfile: (updates: Partial<StudentProfile>) => void;
  addXP: (amount: number) => void;
  updateTopicMastery: (topicId: string, mastery: number) => void;

  // UI
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  // Reset
  resetSelection: () => void;
  resetLearning: () => void;

  // Session Management
  saveCurrentSession: (name?: string) => void;
  loadSession: (sessionId: string) => void;
  deleteSession: (sessionId: string) => void;
  updateCurrentSession: () => void;

  // TTS Auto-play
  setAutoPlayTTS: (autoPlay: boolean) => void;
  setHasPlayedFirstTTS: (hasPlayed: boolean) => void;
}

export const useAppStore = create<AppStore>()(
  persist(
    (set, get) => ({
      // Initial state
      screen: 'welcome',
      language: 'en',
      topicInput: '',
      selectedScope: 'medium',
      subjectMap: null,
      trail: null,
      selectedNodeId: null,
      mode: 'teach',
      boardCells: [],
      messages: [],
      isMapPanelOpen: false,
      profile: createDefaultProfile(),
      isLoading: false,
      error: null,
      savedSessions: [],
      currentSessionId: null,
      autoPlayTTS: false,
      hasPlayedFirstTTS: false,

      // Navigation
      setScreen: (screen) => set({ screen }),
      setLanguage: (language) => set({ language, profile: { ...get().profile, language } }),

      // Subject Selection
      setTopicInput: (topicInput) => set({ topicInput }),
      setSelectedScope: (selectedScope) => set({ selectedScope }),
      setSubjectMap: (subjectMap) => set({ subjectMap }),
      setTrail: (trail) => set({ trail }),
      setSelectedNodeId: (selectedNodeId) => set({ selectedNodeId }),

      // Learning
      setMode: (mode) => set({ mode }),
      addBoardCell: (cell) => set((state) => ({
        boardCells: [...state.boardCells, cell],
      })),
      updateBoardCell: (id, updates) => set((state) => ({
        boardCells: state.boardCells.map((cell) =>
          cell.id === id ? { ...cell, ...updates } : cell
        ),
      })),
      clearBoard: () => set({ boardCells: [] }),
      addMessage: (message) => set((state) => ({
        messages: [...state.messages, message],
      })),
      clearMessages: () => set({ messages: [] }),
      toggleMapPanel: () => set((state) => ({ isMapPanelOpen: !state.isMapPanelOpen })),
      setMapPanelOpen: (isMapPanelOpen) => set({ isMapPanelOpen }),

      // Student Profile
      updateProfile: (updates) => set((state) => ({
        profile: { ...state.profile, ...updates },
      })),
      addXP: (amount) => set((state) => {
        const newXP = state.profile.xp + amount;
        const newLevel = Math.floor(newXP / 100) + 1;
        return {
          profile: { ...state.profile, xp: newXP, level: newLevel },
        };
      }),
      updateTopicMastery: (topicId, mastery) => set((state) => ({
        profile: {
          ...state.profile,
          topicProgress: {
            ...state.profile.topicProgress,
            [topicId]: {
              ...state.profile.topicProgress[topicId],
              mastery,
              attempts: (state.profile.topicProgress[topicId]?.attempts || 0) + 1,
              lastAccessed: Date.now(),
            },
          },
        },
      })),

      // UI
      setLoading: (isLoading) => set({ isLoading }),
      setError: (error) => set({ error }),

      // Reset
      resetSelection: () => set({
        topicInput: '',
        selectedScope: 'medium',
        subjectMap: null,
        trail: null,
        selectedNodeId: null,
        currentSessionId: null,
      }),
      resetLearning: () => set({
        boardCells: [],
        messages: [],
        isMapPanelOpen: false,
        hasPlayedFirstTTS: false,
      }),

      // Session Management
      saveCurrentSession: (name) => {
        const state = get();
        if (!state.subjectMap) return;

        const sessionName = name || state.topicInput || 'Untitled Session';
        const now = Date.now();

        // Check if updating existing session
        if (state.currentSessionId) {
          set({
            savedSessions: state.savedSessions.map((session) =>
              session.id === state.currentSessionId
                ? {
                    ...session,
                    name: sessionName,
                    topicInput: state.topicInput,
                    selectedScope: state.selectedScope,
                    subjectMap: state.subjectMap!,
                    trail: state.trail,
                    selectedNodeId: state.selectedNodeId,
                    mode: state.mode,
                    boardCells: state.boardCells,
                    messages: state.messages,
                    updatedAt: now,
                  }
                : session
            ),
          });
        } else {
          // Create new session
          const newSession: SavedSession = {
            id: crypto.randomUUID(),
            name: sessionName,
            topicInput: state.topicInput,
            selectedScope: state.selectedScope,
            subjectMap: state.subjectMap,
            trail: state.trail,
            selectedNodeId: state.selectedNodeId,
            mode: state.mode,
            boardCells: state.boardCells,
            messages: state.messages,
            createdAt: now,
            updatedAt: now,
          };
          set({
            savedSessions: [newSession, ...state.savedSessions],
            currentSessionId: newSession.id,
          });
        }
      },

      loadSession: (sessionId) => {
        const state = get();
        const session = state.savedSessions.find((s) => s.id === sessionId);
        if (!session) return;

        set({
          topicInput: session.topicInput,
          selectedScope: session.selectedScope,
          subjectMap: session.subjectMap,
          trail: session.trail,
          selectedNodeId: session.selectedNodeId,
          mode: session.mode,
          boardCells: session.boardCells,
          messages: session.messages,
          currentSessionId: sessionId,
          screen: 'learn',
        });
      },

      deleteSession: (sessionId) => {
        const state = get();
        set({
          savedSessions: state.savedSessions.filter((s) => s.id !== sessionId),
          currentSessionId:
            state.currentSessionId === sessionId ? null : state.currentSessionId,
        });
      },

      updateCurrentSession: () => {
        const state = get();
        if (!state.currentSessionId || !state.subjectMap) return;

        set({
          savedSessions: state.savedSessions.map((session) =>
            session.id === state.currentSessionId
              ? {
                  ...session,
                  selectedNodeId: state.selectedNodeId,
                  mode: state.mode,
                  boardCells: state.boardCells,
                  messages: state.messages,
                  updatedAt: Date.now(),
                }
              : session
          ),
        });
      },

      // TTS Auto-play
      setAutoPlayTTS: (autoPlayTTS) => set({ autoPlayTTS }),
      setHasPlayedFirstTTS: (hasPlayedFirstTTS) => set({ hasPlayedFirstTTS }),
    }),
    {
      name: 'teachme-storage',
      partialize: (state) => ({
        language: state.language,
        profile: state.profile,
        savedSessions: state.savedSessions,
        autoPlayTTS: state.autoPlayTTS,
      }),
    }
  )
);
